import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Switch, Typography, Tooltip, IconButton, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, AlertTitle,
  Menu, MenuItem, Autocomplete, TextField
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import ActionConditionsSection from './ActionConditionsSection';
import ActionStatusWindow from './Actions/ActionStatusWindow';
import ActionsLogsDialog from './ActionsLogsDialog';
import ActionAIModelDialog from './Actions/ActionAIModelDialog';
import TagsDropdown from './TagsDropdown';
import {
  useActionCaseId,
  useActionEditorControls,
  useActionEditorGeneralSettings,
  useActionScope,
  useActionSettingsPayload
} from './Actions/ActionScopeContext';
import { fetchFilteredTimeEvents, fetchTimeeventById, fetchTimeeventCount } from '../Utils/timeEventsApi';
import {
  normalizePositiveInteger,
  normalizeNonNegativeInteger,
  FORCE_RUN_SELECTION_ALL,
  FORCE_RUN_SELECTION_FIRST,
  FORCE_RUN_SELECTION_SPECIFIC,
} from '../Utils/forceRunUtils';

/**
 * Complete unified action settings layout
 * Contains everything except the action-specific form fields
 */
const ActionSettingsLayout = ({
  // State
  isEnabled,
  onEnabledChange,
  title,
  isSaveDisabled,
  hasValidationError = false,
  onSave,
  onForceRun,
  forceRunLoading,
  primaryColor = '#5757b0',
  
  // Conditions
  conditions,
  onConditionsChange,
  tags,
  enableConditions = true,
  
  // Status window
  popUpOpen,
  forceRunResult,
  onCloseStatusWindow,
  showEditWithAI = false,
  onEditWithAI,

  allowForceRunWithUnsavedChanges = false,
  
  // Tags dropdown (if needed by action)
  tagsDropdownProps = null,
  
  // Action-specific content (rendered between conditions and bottom)
  children
}) => {
  const { t } = useTranslation();
  const scope = useActionScope();
  const caseid = useActionCaseId();
  const actionSettings = useActionSettingsPayload();
  const registerEditorControls = useActionEditorControls();
  const editorGeneralSettings = useActionEditorGeneralSettings();
  const usesExternalEditorFooter = typeof registerEditorControls === 'function';
  const isEventScope = scope !== 'project';
  const forceRunButtonLabel = t('action_common.force_run', 'Test Run');
  const saveDisabled = isSaveDisabled || hasValidationError;
  const forceRunDisabled = forceRunLoading
    || hasValidationError
    || (!allowForceRunWithUnsavedChanges && !isSaveDisabled);
  const forceRunTooltip = hasValidationError
    ? t('action_common.fix_validation_errors', 'Fix validation errors before running a test.')
    : allowForceRunWithUnsavedChanges
    ? (forceRunLoading
        ? t('action_common.running_test', "Running test...")
        : (isEventScope
          ? t('action_common.force_run_menu_hint', 'Choose how to run this action.')
          : t('action_common.force_run_now_hint', 'Run this action now.')))
    : (!isSaveDisabled
        ? t('action_common.save_first', "Save changes first to enable test run")
        : forceRunLoading
          ? t('action_common.running_test', "Running test...")
          : (isEventScope
            ? t('action_common.force_run_menu_hint', 'Choose how to run this action.')
            : t('action_common.force_run_now_hint', 'Run this action now.')));
  const [forceRunMenuAnchorEl, setForceRunMenuAnchorEl] = useState(null);
  const [forceRunSpecificDialogOpen, setForceRunSpecificDialogOpen] = useState(false);
  const [forceRunSpecificSearchText, setForceRunSpecificSearchText] = useState('');
  const [forceRunSpecificLoading, setForceRunSpecificLoading] = useState(false);
  const [forceRunSpecificOptions, setForceRunSpecificOptions] = useState([]);
  const [forceRunSpecificValue, setForceRunSpecificValue] = useState(null);
  const [forceRunSpecificError, setForceRunSpecificError] = useState('');
  const [forceRunAllWarningOpen, setForceRunAllWarningOpen] = useState(false);
  const [forceRunAllCount, setForceRunAllCount] = useState(null);
  const [forceRunPersistent, setForceRunPersistent] = useState(false);
  const [isLogsDialogOpen, setIsLogsDialogOpen] = useState(false);
  const forceRunSearchAbortRef = useRef(null);
  const actionId = actionSettings?.id;
  const isPersistedAction = Boolean(
    actionId && !(typeof actionId === 'string' && actionId.startsWith('temp_'))
  );
  const actionLogFilters = useMemo(() => (
    isPersistedAction ? { action_id: actionId } : {}
  ), [actionId, isPersistedAction]);

  useEffect(() => {
    setForceRunAllWarningOpen(false);
    setForceRunAllCount(null);
    setIsLogsDialogOpen(false);
  }, [caseid]);

  const closeForceRunMenu = useCallback(() => {
    setForceRunMenuAnchorEl(null);
  }, []);

  const closeForceRunSpecificDialog = useCallback(() => {
    setForceRunSpecificDialogOpen(false);
    setForceRunSpecificSearchText('');
    setForceRunSpecificLoading(false);
    setForceRunSpecificOptions([]);
    setForceRunSpecificValue(null);
    setForceRunSpecificError('');
    if (forceRunSearchAbortRef.current) {
      forceRunSearchAbortRef.current.abort();
      forceRunSearchAbortRef.current = null;
    }
  }, []);

  const formatForceRunTimeeventLabel = useCallback((timeevent) => {
    if (!timeevent) {
      return '';
    }

    const eventSource = String(timeevent.eventsource || '').trim();
    const eventTime = String(timeevent.eventtime || '').trim();
    const parts = [];

    if (eventSource) {
      parts.push(eventSource);
    }
    if (eventTime) {
      parts.push(eventTime);
    }

    return parts.join(' | ') || t('action_common.force_run_specific_event_fallback', 'Time event');
  }, [t]);

  const buildForceRunTimeeventOption = useCallback((timeevent) => {
    const eventId = normalizePositiveInteger(timeevent?.id ?? timeevent?.timeevent_id);
    if (!eventId) {
      return null;
    }

    return {
      id: eventId,
      label: formatForceRunTimeeventLabel(timeevent),
      eventsource: timeevent?.eventsource || '',
      eventtime: timeevent?.eventtime || '',
    };
  }, [formatForceRunTimeeventLabel]);

  const loadForceRunAllCount = useCallback(async () => {
    const normalizedCaseId = normalizePositiveInteger(caseid);
    if (!normalizedCaseId || !isEventScope) {
      return null;
    }

    const response = await fetchTimeeventCount(normalizedCaseId);
    const total = normalizeNonNegativeInteger(
      response?.total
      ?? response?.data?.total
      ?? response?.count
      ?? response?.data?.count
    );

    return total ?? 0;
  }, [caseid, isEventScope]);

  const resolveSpecificTimeeventId = useCallback(() => {
    const selectedId = normalizePositiveInteger(forceRunSpecificValue?.id ?? forceRunSpecificValue);
    if (selectedId) {
      return selectedId;
    }

    return normalizePositiveInteger(forceRunSpecificSearchText);
  }, [forceRunSpecificSearchText, forceRunSpecificValue]);

  const openForceRunSpecificDialog = useCallback(() => {
    closeForceRunMenu();
    setForceRunSpecificError('');
    setForceRunSpecificDialogOpen(true);
  }, [closeForceRunMenu]);

  const handleForceRunButtonClick = useCallback((event) => {
    if (forceRunDisabled) {
      return;
    }

    setForceRunMenuAnchorEl(event.currentTarget);
  }, [forceRunDisabled]);

  const handleForceRunAllConfirm = useCallback(() => {
    setForceRunAllWarningOpen(false);
    onForceRun?.(FORCE_RUN_SELECTION_ALL, null, forceRunPersistent);
  }, [forceRunPersistent, onForceRun]);

  const handleForceRunAllCancel = useCallback(() => {
    setForceRunAllWarningOpen(false);
  }, []);

  const handleForceRunAll = useCallback(async () => {
    closeForceRunMenu();
    if (!isEventScope) {
      onForceRun?.(FORCE_RUN_SELECTION_ALL, null, forceRunPersistent);
      return;
    }

    try {
      const totalCount = forceRunAllCount !== null
        ? forceRunAllCount
        : await loadForceRunAllCount();

      if (totalCount !== null) {
        setForceRunAllCount(totalCount);
      }

      if ((totalCount ?? 0) > 10) {
        setForceRunAllWarningOpen(true);
        return;
      }

      onForceRun?.(FORCE_RUN_SELECTION_ALL, null, forceRunPersistent);
    } catch (error) {
      console.error('Failed to load time event count for force run:', error);
      onForceRun?.(FORCE_RUN_SELECTION_ALL, null, forceRunPersistent);
    }
  }, [closeForceRunMenu, forceRunAllCount, forceRunPersistent, isEventScope, loadForceRunAllCount, onForceRun]);

  const handleForceRunFirst = useCallback(() => {
    closeForceRunMenu();
    onForceRun?.(FORCE_RUN_SELECTION_FIRST, null, forceRunPersistent);
  }, [closeForceRunMenu, forceRunPersistent, onForceRun]);

  const handleForceRunSpecificConfirm = useCallback(() => {
    const selectedTimeeventId = resolveSpecificTimeeventId();
    if (!selectedTimeeventId) {
      setForceRunSpecificError(
        t('action_common.force_run_specific_event_invalid', 'Enter a valid time event id.')
      );
      return;
    }

    closeForceRunSpecificDialog();
    onForceRun?.(FORCE_RUN_SELECTION_SPECIFIC, selectedTimeeventId, forceRunPersistent);
  }, [closeForceRunSpecificDialog, forceRunPersistent, onForceRun, resolveSpecificTimeeventId, t]);

  useEffect(() => {
    if (!forceRunSpecificDialogOpen) {
      return undefined;
    }

    if (!caseid) {
      setForceRunSpecificOptions([]);
      setForceRunSpecificLoading(false);
      setForceRunSpecificError(
        t('action_common.force_run_specific_event_no_case', 'Open a case to search time events.')
      );
      return undefined;
    }

    const query = forceRunSpecificSearchText.trim();
    const abortController = new AbortController();

    if (forceRunSearchAbortRef.current) {
      forceRunSearchAbortRef.current.abort();
    }
    forceRunSearchAbortRef.current = abortController;

    const timeoutId = window.setTimeout(async () => {
      setForceRunSpecificLoading(true);
      try {
        const exactTimeeventId = normalizePositiveInteger(query);
        const filteredResponse = await fetchFilteredTimeEvents(caseid, {
          requestId: `force-run-specific-${caseid}-${query || 'all'}`,
          page: 1,
          pageSize: 10,
          filter: query,
          showHidden: true,
          visibilityFilter: '',
          sortBy: 'date',
          sortOrder: 'desc',
          markAsRead: false,
          signal: abortController.signal,
        });

        const exactTimeevent = exactTimeeventId
          ? await fetchTimeeventById(exactTimeeventId).catch(() => null)
          : null;

        const nextOptions = [];
        const seenIds = new Set();

        const appendTimeevent = (timeevent) => {
          const timeeventCaseId = timeevent?.case_id ?? timeevent?.caseid;
          if (timeeventCaseId != null && Number(timeeventCaseId) !== Number(caseid)) {
            return;
          }
          const option = buildForceRunTimeeventOption(timeevent);
          if (!option || seenIds.has(option.id)) {
            return;
          }
          seenIds.add(option.id);
          nextOptions.push(option);
        };

        if (exactTimeevent) {
          appendTimeevent(exactTimeevent);
        }

        (Array.isArray(filteredResponse?.events) ? filteredResponse.events : []).forEach(appendTimeevent);
        setForceRunSpecificOptions(nextOptions);
        setForceRunSpecificError('');
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        console.error('Failed to search time events for force run:', error);
        setForceRunSpecificOptions([]);
        setForceRunSpecificError(
          t('action_common.force_run_specific_event_search_failed', 'Unable to search time events.')
        );
      } finally {
        if (!abortController.signal.aborted) {
          setForceRunSpecificLoading(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [buildForceRunTimeeventOption, caseid, forceRunSpecificDialogOpen, forceRunSpecificSearchText, t]);

  useEffect(() => {
    if (!isEventScope || !forceRunMenuAnchorEl || forceRunAllCount !== null) {
      return undefined;
    }

    let isCancelled = false;

    loadForceRunAllCount()
      .then((totalCount) => {
        if (!isCancelled && totalCount !== null) {
          setForceRunAllCount(totalCount);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          console.error('Failed to prefetch time event count for force run:', error);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [forceRunAllCount, forceRunMenuAnchorEl, isEventScope, loadForceRunAllCount]);

  const disabledNotice = !isEnabled ? (
    <Alert
      severity="info"
      variant="outlined"
      sx={{
        borderColor: `${primaryColor}30`,
        backgroundColor: `${primaryColor}08`,
        color: '#555',
        borderRadius: '10px',
        '& .MuiAlert-icon': {
          color: primaryColor,
        },
      }}
    >
      <AlertTitle sx={{ color: primaryColor, fontWeight: 700, mb: 0.25 }}>
        {t('action_common.disabled_notice_title', 'Action disabled')}
      </AlertTitle>
      {t(
        'action_common.disabled_notice_body',
        'You can still review and edit the settings before enabling this action.'
      )}
    </Alert>
  ) : null;

  const forceRunButton = useMemo(() => (
    <Tooltip title={forceRunTooltip}>
      <span>
        <Button
          onClick={handleForceRunButtonClick}
          disabled={forceRunDisabled}
          variant="outlined"
          size="small"
          startIcon={<PlayArrowIcon />}
          endIcon={<ArrowDropDownIcon />}
          sx={{
            ml: usesExternalEditorFooter ? 0 : 1.5,
            px: 1.75,
            py: 0.8,
            borderRadius: '10px',
            borderColor: `${primaryColor}50`,
            color: primaryColor,
            textTransform: 'none',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            '&:hover': {
              borderColor: primaryColor,
              backgroundColor: `${primaryColor}08`,
            },
            '&.Mui-disabled': {
              color: '#c0c0d0',
              borderColor: '#e0e0e0',
            },
          }}
        >
          {forceRunButtonLabel}
        </Button>
      </span>
    </Tooltip>
  ), [
    forceRunButtonLabel,
    forceRunDisabled,
    forceRunTooltip,
    handleForceRunButtonClick,
    primaryColor,
    usesExternalEditorFooter,
  ]);

  useEffect(() => {
    if (!usesExternalEditorFooter) {
      return undefined;
    }
    registerEditorControls({
      onSave,
      saveDisabled,
      testRunButton: forceRunButton,
    });
    return () => registerEditorControls(null);
  }, [
    forceRunButton,
    onSave,
    registerEditorControls,
    saveDisabled,
    usesExternalEditorFooter,
  ]);

  return (
    <Box sx={{
      p: 0,
      maxWidth: { xs: '100%', lg: 1000, xl: 1200 },
      mx: 'auto',
      backgroundColor: 'transparent',
    }}>
      <Box sx={{
        overflow: 'hidden',
        borderBottom: isEnabled ? `1px solid ${primaryColor}20` : '1px solid #f0f0f5',
      }}>
        {/* Header with toggle, title, and action buttons */}
        <Box
          data-testid={usesExternalEditorFooter ? 'dag-action-primary-section' : undefined}
          sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1.5, sm: 0 },
          p: { xs: 1.5, sm: 2 },
          mr: usesExternalEditorFooter ? { xs: 2, md: 3 } : 0,
          background: isEnabled
            ? `linear-gradient(145deg, ${primaryColor}08 0%, ${primaryColor}04 100%)`
            : 'linear-gradient(145deg, #f5f5f7 0%, #eeeef0 100%)',
          borderBottom: isEnabled ? `1px solid ${primaryColor}20` : 'none',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <Switch
              checked={isEnabled}
              onChange={(e) => onEnabledChange(e.target.checked)}
              color="primary"
              size="small"
              sx={{
                '& .MuiSwitch-switchBase': {
                  '&.Mui-checked': {
                    color: primaryColor,
                    '& + .MuiSwitch-track': {
                      backgroundColor: primaryColor,
                      opacity: 0.8,
                    },
                  },
                },
                '& .MuiSwitch-thumb': {
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 16,
                  opacity: 0.3,
                },
              }}
            />
            <Typography variant="h6" sx={{
              ml: 1.5,
              fontWeight: 700,
              color: isEnabled ? primaryColor : '#666',
              transition: 'color 0.3s ease',
              letterSpacing: '-0.2px',
              fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
              flexGrow: { xs: 1, sm: 0 }
            }}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' }, width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}>
            <Tooltip title={isPersistedAction
              ? t('workflow.plugin.logs_button', 'Logs')
              : t('workflow.plugin.logs_save_first', 'Save the action before viewing logs')}>
              <span>
                <IconButton
                  onClick={() => setIsLogsDialogOpen(true)}
                  disabled={!isPersistedAction}
                  aria-label={t('workflow.plugin.logs_button', 'Logs')}
                  sx={{
                    color: isPersistedAction ? primaryColor : '#c0c0d0',
                    mr: 0.5,
                    '&:hover': {
                      backgroundColor: `${primaryColor}10`,
                    },
                  }}
                >
                  <HistoryIcon />
                </IconButton>
              </span>
            </Tooltip>
            {showEditWithAI && typeof onEditWithAI === 'function' && (
              <Tooltip title={t('workflow.plugin.edit_with_ai', 'Edit with AI')}>
                <span>
                  <IconButton
                    onClick={onEditWithAI}
                    aria-label={t('workflow.plugin.edit_with_ai', 'Edit with AI')}
                    data-testid="edit-plugin-with-ai-button"
                    sx={{
                      mr: 0.5,
                      color: primaryColor,
                      '&:hover': {
                        backgroundColor: `${primaryColor}10`,
                      },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <ActionAIModelDialog primaryColor={primaryColor} onSave={onSave} />
            {!usesExternalEditorFooter && (
              <Tooltip title={t('action_common.save_settings', 'Save Settings')}>
                <span>
                  <IconButton
                    onClick={onSave}
                    disabled={saveDisabled}
                    aria-label={t('action_common.save_settings', 'Save Settings')}
                    data-testid="action-save-settings-button"
                    sx={{
                      color: saveDisabled ? '#c0c0d0' : primaryColor,
                      '&:hover': {
                        backgroundColor: `${primaryColor}10`,
                      },
                    }}
                  >
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!usesExternalEditorFooter && forceRunButton}
          </Box>
        </Box>

        {/* Content area - always visible so settings can be reviewed before enabling the action */}
        <Box sx={{ 
          p: { xs: 2, md: 3 }, 
          backgroundColor: 'transparent',
          opacity: isEnabled ? 1 : 0.92,
        }}>
          {!usesExternalEditorFooter && disabledNotice && (
            <Box sx={{ mb: 2 }}>
              {disabledNotice}
            </Box>
          )}
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {usesExternalEditorFooter && disabledNotice && (
              <Grid
                item
                xs={12}
                data-testid="dag-action-disabled-section"
                sx={{ pl: '0 !important' }}
              >
                {disabledNotice}
              </Grid>
            )}
            {usesExternalEditorFooter && editorGeneralSettings && (
              <Grid
                item
                xs={12}
                data-testid="dag-action-general-settings-section"
                sx={{ pl: '0 !important' }}
              >
                {editorGeneralSettings}
              </Grid>
            )}
            {/* Conditions editor and action-specific fields stay accessible even when disabled */}
            {enableConditions && (
              <ActionConditionsSection
                conditions={conditions}
                onConditionsChange={onConditionsChange}
                tags={tags}
                flushHorizontalPadding={usesExternalEditorFooter}
              />
            )}

            {/* Action-specific content */}
            {children}
          </Grid>

          {/* Bottom Save Button */}
          {!usesExternalEditorFooter && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', pt: 3, borderTop: '1px solid #f0f0f5' }}>
              <Button
                variant="contained"
                onClick={onSave}
                disabled={saveDisabled}
                startIcon={<SaveIcon />}
                sx={{
                  backgroundColor: primaryColor,
                  color: 'white',
                  px: 4,
                  py: 1,
                  borderRadius: '10px',
                  boxShadow: `0 4px 14px ${primaryColor}40`,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: primaryColor,
                    filter: 'brightness(1.1)',
                    boxShadow: `0 6px 20px ${primaryColor}60`,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#a0a0a0',
                    boxShadow: 'none',
                  }
                }}
              >
                {t('action_common.save_settings', 'Save Settings')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <ActionsLogsDialog
        open={isLogsDialogOpen}
        onClose={() => setIsLogsDialogOpen(false)}
        title={t('workflow.plugin.logs_title', '{{action}} Logs', { action: title })}
        caseid={caseid}
        maxLogs={50}
        extraFilters={actionLogFilters}
      />

      {/* Status Window */}
      <ActionStatusWindow
        isOpen={popUpOpen}
        onClose={onCloseStatusWindow}
        results={forceRunResult}
      />

      <Menu
        anchorEl={forceRunMenuAnchorEl}
        open={Boolean(forceRunMenuAnchorEl)}
        onClose={closeForceRunMenu}
      >
        <MenuItem
          onClick={() => setForceRunPersistent((current) => !current)}
          disabled={forceRunDisabled}
          sx={{ gap: 1 }}
        >
          <Switch size="small" checked={forceRunPersistent} inputProps={{ readOnly: true }} />
          <Box>
            <Typography variant="body2">
              {t('action_common.force_run_persistent', 'Keep running in background')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('action_common.force_run_persistent_hint', 'Continues after you disconnect.')}
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleForceRunAll} disabled={forceRunDisabled}>
          {isEventScope
            ? t('action_common.force_run_all_events', 'Run All Events')
            : t('action_common.force_run_now', 'Run Now')}
        </MenuItem>
        {isEventScope && <MenuItem onClick={handleForceRunFirst} disabled={forceRunDisabled}>
          {t('action_common.force_run_first_event', 'Run First Event')}
        </MenuItem>}
        {isEventScope && <MenuItem onClick={openForceRunSpecificDialog} disabled={forceRunDisabled}>
          {t('action_common.force_run_specific_event', 'Run Specific Event')}
        </MenuItem>}
      </Menu>

      <Dialog
        open={forceRunAllWarningOpen}
        onClose={handleForceRunAllCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: '1px solid #d32f2f40',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon fontSize="small" color="error" />
          {t('action_common.force_run_all_events_warning_title', 'Run all events?')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Alert severity="warning" variant="filled" sx={{ mb: 2, alignItems: 'flex-start' }}>
            <AlertTitle sx={{ fontWeight: 800 }}>
              {t(
                'action_common.force_run_all_events_warning_alert_title',
                'High-volume processing'
              )}
            </AlertTitle>
            {t('action_common.force_run_all_events_warning_body', {
              count: forceRunAllCount ?? 0,
              defaultValue: 'This case has {{count}} time events. Running all events can take a long time and generate a large amount of processing.',
            })}
          </Alert>
          <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5 }}>
            {t(
              'action_common.force_run_all_events_warning_note',
              'Continue only if you really want to process every event.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleForceRunAllCancel}>
            {t('action_common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleForceRunAllConfirm}
          >
            {t(
              'action_common.force_run_all_events_warning_confirm',
              'Run all anyway'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={forceRunSpecificDialogOpen}
        onClose={closeForceRunSpecificDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon fontSize="small" />
          {t('action_common.force_run_specific_event_title', 'Select a Time Event')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5 }}>
            {t(
              'action_common.force_run_specific_event_help',
              'Search by event id, file name, or date, then choose a single time event.'
            )}
          </Typography>

          <Autocomplete
            sx={{ mt: 2 }}
            fullWidth
            openOnFocus
            autoHighlight
            clearOnEscape
            freeSolo
            loading={forceRunSpecificLoading}
            options={forceRunSpecificOptions}
            value={forceRunSpecificValue}
            inputValue={forceRunSpecificSearchText}
            filterOptions={(options) => options}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
            isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
            noOptionsText={t(
              'action_common.force_run_specific_event_no_results',
              'No matching time events.'
            )}
            loadingText={t(
              'action_common.force_run_specific_event_searching',
              'Searching...'
            )}
            onInputChange={(event, value, reason) => {
              setForceRunSpecificSearchText(value);
              setForceRunSpecificError('');
              if (reason === 'input' || reason === 'clear') {
                setForceRunSpecificValue(null);
              }
            }}
            onChange={(event, newValue) => {
              setForceRunSpecificValue(newValue);
              setForceRunSpecificError('');
              if (typeof newValue === 'string') {
                setForceRunSpecificSearchText(newValue);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    #{option.id}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      lineHeight: 1.3,
                      wordBreak: 'break-word',
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('action_common.force_run_specific_event_label', 'Time Event')}
                placeholder={t(
                  'action_common.force_run_specific_event_placeholder',
                  'Search time events...'
                )}
                error={Boolean(forceRunSpecificError)}
                helperText={
                  forceRunSpecificError || t(
                    'action_common.force_run_specific_event_hint',
                    'You can search by event id or source name.'
                  )
                }
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForceRunSpecificDialog}>
            {t('action_common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleForceRunSpecificConfirm}
            disabled={forceRunSpecificLoading || !resolveSpecificTimeeventId()}
          >
            {t('action_common.force_run_specific_event_confirm', 'Run Selected Event')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Optional Tags Dropdown */}
      {tagsDropdownProps && (
        <TagsDropdown {...tagsDropdownProps} />
      )}
    </Box>
  );
};

export default ActionSettingsLayout;
